/**
 * admin-ops-api の `/api/v1/feature-usage`（機能利用）client。#1959・O31-7。
 * `ai-usage-api.ts` と同じ fetch ラッパの形。表の中身（機能名・並び・区分・ラベル）は API 側（`@aic/core`）で決まり、
 * 画面は受け取って描くだけ（admin-ops-web は `@aic/core` に依存しない）。型は API の応答を写したもの。
 */

export type UsageSide = 'customer' | 'alys';
export type UsageTab = 'features' | 'screens' | 'transitions' | 'abandoned' | 'unused';
export type FeatureUsagePeriod = 'today' | 'yesterday' | '7d' | '28d' | 'range' | 'compare';

export interface DayRange {
  fromDay: string;
  toDay: string;
}

export interface SideStats {
  workspaces: number;
  users: number;
  days: number;
  clicks: number;
  views: number;
  errors: number;
  lastDay: string | null;
}

export interface FeatureTableRow {
  key: string;
  kind: 'feature' | 'unclassified' | 'noIdentifier';
  label: string;
  identifier: string | null;
  inPeriod: boolean;
  customer: SideStats;
  alys: SideStats;
  abandoned: { customer: number; alys: number } | null;
  workspaces: Array<{
    workspaceId: string;
    name: string | null;
    side: UsageSide;
    lastDay: string | null;
    days: number;
    clicks: number;
    views: number;
    abandoned: number | null;
    errors: Record<string, number>;
  }>;
  parts: Array<{
    routePattern: string;
    elementKey: string | null;
    elementKind: string | null;
    customer: number;
    alys: number;
  }>;
}

interface ScreenSide {
  views: number;
  users: number;
  dwellMedianMs: number | null;
  lastDay: string | null;
  errors: number;
}

export interface ScreenTableRow {
  routePattern: string;
  feature: string | null;
  customer: ScreenSide;
  alys: ScreenSide;
}

/** 行き先の `routePattern: null` は `その後の操作なし`。 */
export interface PairCount {
  routePattern: string | null;
  customer: number;
  alys: number;
}

export interface TransitionTable {
  origin: string;
  sources: PairCount[];
  destinations: PairCount[];
}

export interface FlowTableRow {
  key: string;
  label: string;
  feature: string | null;
  customer: { started: number; completed: number; abandoned: number };
  alys: { started: number; completed: number; abandoned: number };
  destinations: PairCount[];
}

export interface UnusedRow {
  key: string;
  label: string;
  kind: 'mapped' | 'recorded';
  customerLastDay: string | null;
  alysLastDay: string | null;
}

export interface FeatureUsageTables {
  features: FeatureTableRow[];
  screens: ScreenTableRow[];
  transitions: TransitionTable[];
  flows: FlowTableRow[];
  unused: UnusedRow[];
  collection: { customer: 'not_started' | 'collecting'; sinceDay: string | null };
  meta: {
    recordDays: number;
    customerWorkspaces: number;
    total: number;
    alysDefined: boolean;
    workspace: { workspaceId: string; name: string | null; side: UsageSide } | null;
  };
}

export interface FeatureUsageConfigured {
  configured: true;
  period: FeatureUsagePeriod;
  range: DayRange;
  asOf: string;
  compare: {
    a: DayRange;
    b: DayRange;
    base: string | null;
    days: number | null;
    sameLength: boolean;
  } | null;
  tables: FeatureUsageTables;
  previousTables: FeatureUsageTables | null;
  alysWorkspaces: Array<{ workspaceId: string; name: string | null }>;
  captureStartedAt: string | null;
  mappingUpdatedAt: string;
  rawRetentionFrom: string;
  mapping: {
    features: Array<{ feature: string; screens: string[]; elements: string[] }>;
    flows: Array<{
      key: string;
      label: string;
      startElements: string[];
      completeAuditActions: string[];
    }>;
  };
}

export type FeatureUsageResult = FeatureUsageConfigured | { configured: false };

export interface UsageInsightItem {
  kind: 'observation' | 'candidate';
  text: string;
  label: string;
  evidence: {
    target: string;
    counts: string;
    segment: UsageSide;
    customerWorkspaces: number;
    alysIncluded: boolean;
    fromDay: string;
    toDay: string;
    link: { tab: UsageTab; workspaceId: string | null };
  };
}

export interface UsageInsightResult {
  configured: true;
  day: string;
  latestDay: string | null;
  previousDay: string;
  nextDay: string | null;
  scheduledAt: string;
  insight: {
    status: string;
    items: UsageInsightItem[];
    droppedCount: number;
    customerCaptureStarted: boolean;
    generatedAt: string;
    slackSentAt: string | null;
  } | null;
}

/** 取得の失敗。権限なし（再試行しても直らない）とそれ以外を言い分ける。 */
export class FeatureUsageFetchError extends Error {
  constructor(readonly kind: 'permission_denied' | 'failed') {
    super(kind === 'permission_denied' ? 'permission_denied' : 'fetch_failed');
    this.name = 'FeatureUsageFetchError';
  }
}

async function getJson<T>(baseUrl: string, path: string, params: URLSearchParams): Promise<T> {
  const qs = params.toString();
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}${path}${qs ? `?${qs}` : ''}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (res.status === 403) throw new FeatureUsageFetchError('permission_denied');
  if (!res.ok) throw new FeatureUsageFetchError('failed');
  return (await res.json()) as T;
}

/** 期間のクエリ（`period` / `from` / `to` / `base` / `days` / `aFrom` … / `workspace`）をそのまま渡す。 */
export function fetchFeatureUsage(
  baseUrl: string,
  query: Record<string, string>,
): Promise<FeatureUsageResult> {
  return getJson(baseUrl, '/api/v1/feature-usage', new URLSearchParams(query));
}

export function fetchUsageInsight(
  baseUrl: string,
  day: string | null,
): Promise<UsageInsightResult | { configured: false }> {
  const params = new URLSearchParams();
  if (day) params.set('day', day);
  return getJson(baseUrl, '/api/v1/feature-usage/insights', params);
}
